// Lectura defensiva del documento argus.hallazgos/1 (diseño 4a, §2.1). La ficha sólo pinta un documento que pasa esta lectura; si
// no, dice «No pudimos leer esta ficha.» y no pinta nada a medias. No reemplaza al esquema (eso lo prueba hallazgos_contrato_test.ts
// con ajv): comprueba lo que la pantalla necesita para no romperse, los campos del lote 0a (una base sin el lote no sirve), los
// arreglos, las referencias cruzadas y que el nivel sea conocido. Sin dependencias: corre en la SPA.
import type { Hallazgos } from './tipos.ts';

const NIVELES = ['no_operar', 'muy_grave', 'grave', 'revisar', 'por_confirmar', 'sin_hallazgos'];
const SENTIDOS = ['sale', 'llega', 'mutuo', 'desconocido'];
const ESTADOS_LISTA = ['en_lista', 'cuenta_como_sancionada', 'retirada', 'posible', 'no_esta'];

type O = Record<string, unknown>;
const esObj = (x: unknown): x is O => typeof x === 'object' && x !== null && !Array.isArray(x);
const esTexto = (x: unknown): x is string => typeof x === 'string' && x.length > 0;
const esArr = Array.isArray;

function nivelOk(n: unknown): boolean {
  return esObj(n) && typeof n.valor === 'string' && NIVELES.includes(n.valor) && esTexto(n.etiqueta) && esTexto(n.porque) && esArr(n.ajustes);
}

function estadoListaOk(e: unknown): boolean {
  return esObj(e) && typeof e.estado === 'string' && ESTADOS_LISTA.includes(e.estado) && esTexto(e.texto) && esArr(e.programas);
}

const sentidoOk = (s: unknown) => typeof s === 'string' && SENTIDOS.includes(s);

/** El documento tipado si se puede pintar; null si no. */
export function leerHallazgos(x: unknown): Hallazgos | null {
  if (!esObj(x) || x.contrato !== 'argus.hallazgos/1' || !esTexto(x.generado)) return null;
  if (!esObj(x.cortes) || !esObj(x.sujeto) || !esObj(x.omitido) || !esObj(x.sancionados)) return null;
  if (!esArr(x.hallazgos) || !esArr(x.huecos) || !esArr(x.que_hacer) || !esArr(x.tambien)) return null;
  if (!nivelOk(x.nivel) || !estadoListaOk(x.en_lista)) return null;
  const s = x.sujeto;
  if (!esTexto(s.ref) || !esTexto(s.nombre) || !esTexto(s.nombre_corto) || (s.tipo !== 'persona' && s.tipo !== 'empresa') || !esArr(s.datos)) return null;
  // Lote 0a: la agrupación lleva personas y frase; la tarjeta unida existe (puede ser null).
  if (!('coinciden_con_lista' in s) || !('agrupado' in s)) return null;
  if (s.agrupado !== null && (!esObj(s.agrupado) || typeof s.agrupado.personas !== 'number' || !esTexto(s.agrupado.texto))) return null;
  // Etapa 5, ficha de grupo: `miembros` es opcional; si viene, de 2 a 20 registros sin repetir y el primero es el sujeto.
  if ('miembros' in s) {
    const m = s.miembros;
    if (!esArr(m) || m.length < 2 || m.length > 20) return null;
    if (!m.every((x) => esObj(x) && esTexto(x.ref) && esTexto(x.nombre))) return null;
    if (new Set(m.map((x) => (x as O).ref)).size !== m.length || (m[0] as O).ref !== s.ref) return null;
  }
  if (typeof x.omitido.hallazgos !== 'number' || typeof x.omitido.relaciones_lejanas !== 'number') return null;

  const ids = new Set<string>();
  for (const h of x.hallazgos) {
    if (!esObj(h) || !esTexto(h.id) || typeof h.peso !== 'number' || !nivelOk(h.nivel) || !esArr(h.huecos) || !esArr(h.frases) || !esArr(h.episodios)) return null;
    if (!esObj(h.contraparte) || !esTexto(h.contraparte.ref) || !(h.contraparte.ref in x.sancionados) || !esTexto(h.contraparte.nombre)) return null;
    if (!esObj(h.relacion) || !esTexto(h.relacion.frase) || !esTexto(h.relacion.texto) || !esArr(h.relacion.a_traves_de)) return null;
    for (const e of h.episodios) if (!esObj(e) || !sentidoOk(e.sentido) || !esTexto(e.papel_texto) || !esObj(e.fuente)) return null;
    for (const p of h.relacion.a_traves_de) {
      if (!esObj(p) || !sentidoOk(p.sentido) || !esTexto(p.papel_texto) || !esTexto(p.nombre) || !estadoListaOk(p.en_lista)) return null;
      if (p.siguiente !== undefined && (!esObj(p.siguiente) || !sentidoOk(p.siguiente.sentido) || !esTexto(p.siguiente.papel_texto))) return null;
    }
    if (ids.has(h.id)) return null;
    ids.add(h.id);
  }
  const qs = new Set<string>();
  for (const q of x.huecos) {
    if (!esObj(q) || !esTexto(q.id) || !esTexto(q.texto) || !esArr(q.afecta)) return null;
    qs.add(q.id);
  }
  // Referencias cruzadas: cada hueco de un hallazgo existe y cada hallazgo afectado existe.
  for (const h of x.hallazgos as O[]) for (const q of h.huecos as unknown[]) if (typeof q !== 'string' || !qs.has(q)) return null;
  for (const q of x.huecos as O[]) for (const h of q.afecta as unknown[]) if (typeof h !== 'string' || !ids.has(h)) return null;
  for (const v of Object.values(x.sancionados)) {
    if (!esObj(v) || !esTexto(v.nombre) || !estadoListaOk(v.en_lista) || !esObj(v.por_que) || !esArr(v.por_que.historia) || !esArr(v.por_que.organigramas)) return null;
  }
  return x as unknown as Hallazgos;
}
